/**
 * Estado "enganchado al fondo" del hilo de conversación, como máquina pura
 * (testeable sin DOM ni sesión de voz). Lo maneja `useConversationController`.
 *
 * ─── El problema que resuelve ────────────────────────────────────────────
 * Mientras el coach habla, el controlador escribe `scrollTop = scrollHeight`
 * en CADA frame (rAF) para que el karaoke no se vaya abajo del fold. Si la
 * única forma de soltar ese seguimiento fuera estar a más de N px del fondo
 * (el umbral viejo: 120 px), el usuario NUNCA llega: cada frame gana el delta
 * de su gesto (una muesca de rueda, unos px de arrastre) y el rAF del frame
 * siguiente se lo devuelve antes de cruzar el umbral. Síntomas reportados
 * (2026-09-01): "al querer scrollear arriba la app me devuelve abajo" y
 * "directamente me bloquea poder subir".
 *
 * ─── La salida, en dos capas ─────────────────────────────────────────────
 * 1. **El gesto, antes de que el browser lo aplique** (`releaseByGesture`,
 *    que el controlador cablea a `wheel`/`touchmove`): es la capa que evita
 *    la fricción. Cualquier detección posterior llega tarde por definición —
 *    el scroll ya se movió y el rAF alcanzó a devolverlo al fondo al menos
 *    una vez, que es lo que se siente como vibración del último recuadro.
 * 2. **La deriva del `scrollTop`** (`reducePin`): red de seguridad para todo
 *    lo que no pasa por esos eventos — arrastrar la barra de scroll, el
 *    teclado, un scroll programático de otro componente.
 *
 * La capa 2 distingue QUIÉN movió el scroll en vez de mirar la distancia:
 *
 * - El controlador anota en `autoTop` el `scrollTop` REAL que dejó cada
 *   escritura nuestra (leído del DOM, ya clampeado). Sin usuario de por
 *   medio, el próximo read da exactamente ese valor.
 * - Un `scrollTop` MENOR al esperado es movimiento que no hicimos nosotros →
 *   intención del usuario de subir → soltamos el hilo al instante, sin
 *   umbral de distancia. Se acumula en `upDrift` para que también cuente un
 *   arrastre lento (1-2 px por frame) que el rAF revierte cada vez.
 * - Los clamps del browser NO son el usuario: si el contenido se achica o el
 *   viewport crece (teclado que se cierra), `scrollTop` baja solo. Por eso el
 *   esperado se compara contra el máximo scrolleable de ESTE layout.
 * - Al revés, el contenido creciendo abajo (burbuja que streamea, karaoke que
 *   revela palabras) ya no suelta el hilo: antes la distancia al fondo crecía
 *   sola y disparaba el chip "volver al final" sin que el usuario tocara nada.
 * - El clamp que NO vemos: si el contenido se achica y vuelve a crecer dentro
 *   del mismo frame (un layout forzado a mitad de camino lo recorta; la charla
 *   asistida, al empezar la línea nueva del coach, encargo 113), la próxima
 *   lectura ya ve el contenido grande con el `scrollTop` recortado — idéntico a
 *   un usuario que subió. Ahí decide `scrollInput` (ver {@link ReducePinOptions}).
 *
 * ─── Histéresis ──────────────────────────────────────────────────────────
 * Soltar es instantáneo; re-enganchar pide volver PEGADO al fondo
 * (`REPIN_PX`). Con un único umbral simétrico y ancho, el mismo gesto que
 * suelta el hilo lo re-engancharía en el frame siguiente (sigue dentro de los
 * 120 px) y el rAF lo tiraría abajo otra vez — el efecto "me devuelve abajo".
 */

/** Métricas del contenedor scrolleable (un `HTMLElement` las satisface). */
export interface ThreadMetrics {
  readonly scrollTop: number;
  readonly scrollHeight: number;
  readonly clientHeight: number;
}

export interface ThreadPinState {
  /** ¿El hilo sigue el contenido nuevo? */
  readonly pinned: boolean;
  /** Último `scrollTop` que dejó una escritura NUESTRA (o el confirmado al re-enganchar). */
  readonly autoTop: number;
  /** Píxeles hacia arriba acumulados que no explican nuestras escrituras. */
  readonly upDrift: number;
  /** Gesto del usuario en curso (rueda o dedo): no re-enganchamos hasta que termine. */
  readonly driving: boolean;
}

/**
 * Movimiento hacia arriba (acumulado entre escrituras nuestras) que cuenta
 * como "el usuario quiere leer". Sin usuario el drift es exactamente 0 — no
 * es un umbral de tolerancia sino un filtro de subpíxel/redondeo, por eso es
 * chico: cualquier gesto real lo cruza en uno o dos frames.
 */
export const USER_INTENT_PX = 4;

/**
 * Distancia al fondo para volver a enganchar por scroll propio (el chip
 * "volver al final" es el otro camino). Chico a propósito: ver la histéresis
 * en el header.
 */
export const REPIN_PX = 24;

export const INITIAL_PIN_STATE: ThreadPinState = {
  pinned: true,
  autoTop: 0,
  upDrift: 0,
  driving: false,
};

/** Máximo `scrollTop` posible en este layout. */
export function maxScrollTop(m: ThreadMetrics): number {
  return Math.max(0, m.scrollHeight - m.clientHeight);
}

export interface ReducePinOptions {
  /**
   * ¿Hubo algo del usuario que pueda mover el scroll (rueda, arrastre, teclas,
   * la barra apretada)? Con `false`, una deriva hacia arriba es del browser y
   * no suelta el hilo. Ausente = puede haberlo (la vara de siempre: cualquier
   * scroll que no hicimos nosotros, también el programático de otro componente).
   */
  readonly scrollInput?: boolean;
}

/**
 * Relee el contenedor y decide si seguimos enganchados. Se llama tanto desde
 * el `onScroll` del hilo como desde el loop de rAF ANTES de escribir (un
 * gesto puede no haber emitido su evento de scroll todavía).
 */
export function reducePin(
  prev: ThreadPinState,
  m: ThreadMetrics,
  opts: ReducePinOptions = {},
): ThreadPinState {
  const maxTop = maxScrollTop(m);
  const top = m.scrollTop;
  // Lo que valdría `scrollTop` si nadie lo tocó desde nuestra última
  // escritura. El `min` absorbe el clamp del browser (contenido que se achica
  // / viewport que crece): eso no es el usuario.
  const expected = Math.min(prev.autoTop, maxTop);
  const drift = expected - top;

  if (prev.pinned) {
    const upDrift = drift > 0 ? prev.upDrift + drift : 0;
    if (upDrift > USER_INTENT_PX) {
      // Nadie pudo mover el scroll: fue un recorte del browser. Seguimos
      // pegados, contra el valor de ahora (la próxima escritura baja al fondo).
      if (opts.scrollInput === false) {
        return { pinned: true, autoTop: top, upDrift: 0, driving: prev.driving };
      }
      return { pinned: false, autoTop: top, upDrift: 0, driving: prev.driving };
    }
    return { pinned: true, autoTop: expected, upDrift, driving: prev.driving };
  }

  // Suelto: sólo re-engancha si volvió pegado al fondo por su cuenta, y NUNCA
  // en medio de un gesto — arrancar a scrollear desde el fondo pasa por los
  // primeros píxeles, y re-enganchar ahí sería el tirón hacia abajo (con su
  // vibración) apenas empezás a subir.
  // `autoTop: top` en la transición es clave — si no, el evento de scroll
  // siguiente compararía contra un `autoTop` viejo, leería ese hueco como
  // intención de subir y lo soltaría de nuevo (parpadeo del chip).
  if (!prev.driving && maxTop - top <= REPIN_PX) {
    return { pinned: true, autoTop: top, upDrift: 0, driving: false };
  }
  return { pinned: false, autoTop: expected, upDrift: 0, driving: prev.driving };
}

/**
 * Intención explícita de subir, leída del gesto (rueda hacia arriba, dedo
 * arrastrando hacia abajo) ANTES de que el browser lo aplique.
 *
 * Es la diferencia entre soltar el hilo con cero fricción y soltarlo un par de
 * frames tarde: `reducePin` sólo puede reaccionar DESPUÉS de que el scroll ya
 * se movió, y en esos frames el loop de rAF todavía devuelve el hilo al fondo
 * — el usuario ve el último recuadro vibrando mientras empuja.
 */
export function releaseByGesture(prev: ThreadPinState): ThreadPinState {
  return { pinned: false, autoTop: prev.autoTop, upDrift: 0, driving: true };
}

/** Fin del gesto (dedo levantado / la rueda dejó de emitir). Habilita el re-enganche. */
export function endGesture(prev: ThreadPinState): ThreadPinState {
  return { ...prev, driving: false };
}

/**
 * Tras escribir el scroll nosotros: `top` es el valor real que quedó en el DOM.
 *
 * `upDrift` SOBREVIVE a la escritura a propósito. Mientras el hilo está pegado
 * escribimos cada frame, así que resetear acá borraría el acumulado antes de
 * que sirva: un arrastre lento (1-2 px por frame, por debajo del umbral) nunca
 * llegaría a soltar el hilo y el usuario quedaría trabado contra el rAF. Lo
 * limpia `reducePin` en cuanto un frame no muestra movimiento hacia arriba.
 */
export function markAutoScrolled(prev: ThreadPinState, top: number): ThreadPinState {
  return { pinned: prev.pinned, autoTop: top, upDrift: prev.upDrift, driving: prev.driving };
}
